import React, { useState } from "react";
import "./SetPassword.css";
import { Button, TextField, InputAdornment, IconButton, Typography } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

interface SetPasswordProps {
  onPasswordSet: (password: string) => void;
  onBack: () => void;
  clientId?: string | number;
}

const passwordRules = [
  { regex: /.{8,}/, message: "At least 8 characters" },
  { regex: /[a-z]/, message: "A lowercase letter" },
  { regex: /[A-Z]/, message: "An uppercase letter" },
  { regex: /[0-9]/, message: "A number" },
  {
    regex: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/,
    message: "A special character",
  },
];

const SetPassword: React.FC<SetPasswordProps> = ({ onPasswordSet, onBack, clientId }) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showFormatError, setShowFormatError] = useState(false);
  const [showMatchError, setShowMatchError] = useState(false);
  const [showPassword, setShowPassword] = useState({
    password: false,
    confirmPassword: false,
  });

  const validatePassword = (pwd: string) =>
    passwordRules.every((rule) => rule.regex.test(pwd));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowFormatError(false);
    setShowMatchError(false);
    if (!validatePassword(password)) {
      setShowFormatError(true);
      return;
    }
    if (password !== confirmPassword) {
      setShowMatchError(true);
      return;
    }
    
    // Store password in localStorage (will be sent to server at final step)
    const pendingRegKey = `pendingRegistration_${clientId}`;
    const pendingRegStored = localStorage.getItem(pendingRegKey);
    if (pendingRegStored) {
      try {
        const pendingReg = JSON.parse(pendingRegStored);
        pendingReg.password = password;
        pendingReg.step = 'twoFactorSetup';
        localStorage.setItem(pendingRegKey, JSON.stringify(pendingReg));
      } catch {
        console.error("Failed to update pending registration with password");
      }
    }
    
    onPasswordSet(password);
  };

  return (
    <div
      style={{
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
      }}
    >
      <h3 style={{ marginBottom: 0, color: "#888" }}>Set Your Password</h3>
      <div
        style={{
          fontSize: "13px",
          width: "100%",
          textAlign: "center",
          color: "#666",
        }}
      >
        <p style={{ marginBottom: 0 }}>
          To help keep your account secure, please choose a password that is not
          <br />
          easily guessed.
        </p>
      </div>
      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          width: "280px",
        }}
      >
        <ul className="password-rules">
          {passwordRules.map((rule) => (
            <li
              key={rule.message}
              className={password && rule.regex.test(password) ? "valid" : ""}
            >
              {rule.message}
            </li>
          ))}
        </ul>
        <TextField
          type={showPassword.password ? "text" : "password"}
          label="Password"
          name="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          required
          autoFocus
          inputProps={{ maxLength: 64 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() =>
                    setShowPassword({
                      ...showPassword,
                      password: !showPassword.password,
                    })
                  }
                  edge="end"
                >
                  {showPassword.password ? (
                    <VisibilityOffIcon />
                  ) : (
                    <VisibilityIcon />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            width: "100%",
            maxWidth: 300,
            marginBottom: 1.5,
          }}
        />
        <TextField
          type={showPassword.confirmPassword ? "text" : "password"}
          label="Confirm Password"
          name="confirmPassword"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          fullWidth
          required
          inputProps={{ maxLength: 64 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() =>
                    setShowPassword({
                      ...showPassword,
                      confirmPassword: !showPassword.confirmPassword,
                    })
                  }
                  edge="end"
                >
                  {showPassword.confirmPassword ? (
                    <VisibilityOffIcon />
                  ) : (
                    <VisibilityIcon />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            width: "100%",
            maxWidth: 300,
            marginBottom: 1.5,
          }}
        />
        <Button
          type="submit"
          variant="contained"
          size="large"
          sx={{
            width: "100%",
            backgroundColor: "var(--color-primary)",
            "&:hover": { backgroundColor: "var(--color-secondary)" },
          }}
        >
          Continue
        </Button>
        <Typography
          component="p"
          variant="body2"
          onClick={onBack}
          sx={{ 
            cursor: "pointer",
            color: "var(--color-primary)",
            textAlign: "center",
            marginTop: "10px",
            "&:hover": { color: "var(--color-secondary)" }
          }}
        >
          Back to registration page
        </Typography>
       
        {showFormatError && (
          <div className="alert-message">Password format not valid</div>
        )}
        {showMatchError && (
          <div className="alert-message">Passwords do not match</div>
        )}
      </form>
    </div>
  );
};

export default SetPassword;
